/**
 * The frame every screen renders inside.
 *
 * Mobile-first: designed at 390px, then widened. Desktop
 * is the same information in a wider column, not a second layout.
 *
 * There is no light/dark toggle here or anywhere else. The OS
 * color scheme is the only source of truth, and every
 * color below is a theme token utility, never a raw hex.
 *
 * The wordmark row is at the top at every width. The section row under it
 * is the same element at every width too, but under 640px `.sm-section-nav`
 * in `index.css` fixes it to the bottom of the viewport instead, where a
 * fan's thumb is (ISSUE-953). That is a placement change in one stylesheet
 * rule, not a second navigation: nothing here renders twice, so nothing here
 * can announce the four sections twice to a screen reader.
 *
 * `sm-screen-bottom` on the scroll container below is what keeps content
 * clear of that bar. It was already there, it is still applied exactly once,
 * and no screen pairs it with a bottom padding of its own.
 *
 * Under 640px the wordmark row also drops Sign out, so the wordmark, the
 * bell and Account fit on one row (ISSUE-953). `.sm-header-sign-out` in
 * index.css does it, and the class is applied only once onboarding is over:
 * during onboarding Sign out is the fan's only way off a wrong account. On a
 * phone Sign out lives on the Account screen.
 *
 * One kind of screen renders without the header: a pool takes over the top of
 * the viewport with its own back arrow, title and share control (ISSUE-952
 * R1). `chromelessRoutes.ts` is that opt-out, a pure predicate over the
 * location. The section bar stays on that screen at phone width (ISSUE-965):
 * it is rendered after `<main>` in a wrapper that is `sm:hidden`, so under
 * 640px it is the same fixed bottom bar as everywhere else and at 640px and
 * up the pool screen has no section navigation, as before. It is still the
 * one `SectionNav`, never a second copy.
 */
import { Button } from "@sportsmet/ui";
import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useAuth } from "../auth/AuthProvider";
import { OfflineBanner } from "../connectivity/OfflineBanner";
import { ONBOARDING_PATH } from "../features/onboarding/onboardingSteps";
import { isChromelessLocation } from "./chromelessRoutes";
import { HomeLink } from "./HomeLink";
import { NotificationBell } from "./NotificationBell";
import { SectionNav } from "./SectionNav";
import { resolveDocumentTitle, useDocumentTitle } from "./sections";

const Wordmark = ({ isLinked }: { isLinked: boolean }): ReactNode =>
  isLinked ? (
    // Signed out, this goes to the marketing site: app `/` would only
    // return the visitor to sign-in (ISSUE-1101 R5).
    <HomeLink
      // 44px minimum is the WCAG 2.2 touch-target floor, measured by
      // tests/e2e/src/app/touchTargets.e2e.ts.
      className="flex min-h-touch-target items-center text-h2 text-ink no-underline"
      aria-label="Sportsmet home"
    >
      Sportsmet
    </HomeLink>
  ) : (
    // A link to `/` during onboarding is bounced straight back by
    // `OnboardingGate`, so it reads as a broken control. Plain text instead.
    <span className="flex min-h-touch-target items-center text-h2 text-ink">
      Sportsmet
    </span>
  );

export const AppShell = ({ children }: { children: ReactNode }): ReactNode => {
  const { state, signOut, isSigningOut } = useAuth();
  const router = useRouter();
  const pathname = useRouterState({
    select: (routerState) => routerState.location.pathname,
  });
  // A primitive, so this selector cannot churn the way a structural compare
  // over the whole search object would.
  const isChromeless = useRouterState({
    select: (routerState) =>
      isChromelessLocation({
        pathname: routerState.location.pathname,
        search: routerState.location.search,
      }),
  });
  useDocumentTitle(resolveDocumentTitle(pathname));
  const isSignedIn = state.status === "signedIn";
  const isOnboarding = pathname.startsWith(ONBOARDING_PATH);

  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      {isChromeless ? null : (
        <header className="border-b border-hairline bg-surface">
          <div className="sm-content-width px-page-gutter">
            <div className="flex min-h-touch-target flex-wrap items-center justify-between gap-3 py-2">
              <Wordmark isLinked={!isOnboarding} />
              {isSignedIn ? (
                // `flex-wrap` is load-bearing here, not decoration.
                // The theme fixes text sizes in px but keeps spacing in rem
                // (`packages/theme/tailwind-preset.css`). So raising the root
                // font grows every `gap-*` and `px-*` in this row.
                // The labels themselves stay exactly as wide as they were.
                // The header's inset is the fixed 16px page gutter, so it does not grow with the root font.
                // `Button` is `whitespace-nowrap`, and `NotificationBell`
                // carries the unbreakable word "Notifications".
                // Without wrapping, nothing can break to a second line.
                // The document then runs 50px past a 390px viewport
                // at the largest text scale (ISSUE-791).
                // Every sibling row in the shell already wraps.
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  {isOnboarding ? null : <NotificationBell />}
                  {isOnboarding ? null : (
                    <Button variant="ghost" size="sm" asChild>
                      <Link to="/account" data-testid="shell-account-link">
                        Account
                      </Link>
                    </Button>
                  )}
                  {/* Kept during onboarding on purpose. It is the fan's way off a wrong account, which is what owner answer Q272 asked for, and it produces open decision D3's recommended outcome: sign out, the account survives, onboardingStep resumes it next time. `router.invalidate()` after `signOut()` is what makes that outcome real: it re-runs `beforeLoad` on the current match, and `requireSignedIn` already redirects to `/sign-in` once the cookie is gone, carrying the current href so signing back in resumes the same step. */}
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={isSigningOut}
                    loadingLabel="Signing out"
                    className={isOnboarding ? "" : "sm-header-sign-out"}
                    data-testid="shell-sign-out"
                    onClick={() => {
                      void signOut().then(() => router.invalidate());
                    }}
                  >
                    Sign out
                  </Button>
                </div>
              ) : null}
            </div>
            {isSignedIn && !isOnboarding ? <SectionNav /> : null}
          </div>
        </header>
      )}

      <OfflineBanner />

      {/*
        `sm-screen-bottom` is the shared 64px +
        safe-area reserve. It is applied once, here, on the scroll container —
        never re-declared as a per-screen padding value.
      */}
      <main className="sm-screen-bottom sm-content-width flex-1 px-page-gutter pt-6">
        {children}
      </main>
      {isChromeless && isSignedIn && !isOnboarding ? (
        <div className="sm:hidden" data-testid="pool-screen-section-nav">
          <SectionNav />
        </div>
      ) : null}
    </div>
  );
};
